import React, { useState, useCallback, useContext } from 'react';
import clsx from 'clsx';

import { useDropzone } from 'react-dropzone';

import {
    Button,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Tooltip,
    CircularProgress,
    colors,
    Modal,
    Box
} from '@material-ui/core';

import PublishIcon from '@material-ui/icons/Publish';
import DeleteIcon from '@material-ui/icons/Delete';

import SaveIcon from '@material-ui/icons/Save';
import ClearIcon from '@material-ui/icons/Clear';

import { makeStyles } from '@material-ui/styles';
const useStyles = makeStyles((theme) => ({
    root: {

    },
    dropZone: {
        border: "1px",
        padding: "8px",
        outline: "none",
        display: "flex",
        flexWrap: "wrap",
        backgroundColor: "#e1e1e1",
        opacity: 1,
        "&:hover": {
            backgroundColor: "#f1f1f1",
            opacity: 1,
            cursor: "pointer"
        }
    },
    dragActive: {
        backgroundColor: colors.grey[50],
        opacity: 0.5
    },
    item: {
    },
    image: {
        width: 130
    },
    info: {
        marginTop: 1
    },
    list: {
        maxHeight: 320
    },
    thumbnail: {
        width: "48px",
        height: "48px",
        marginRight: theme.spacing(1)
    },
    actions: {
        marginTop: 2,
        display: "flex",
        justifyContent: "flex-end",
        "& > * + *": {
            marginLeft: 2
        }
    },
    removeButton: {
        marginRight: 10
    },
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "auto",
        backgroundColor: '#ffffff'
    },
    modalTitle: {
        backgroundColor: '#f5f2f0',
        fontSize: '25px',
        padding: '20px'
    },
    modalText: {
        fontSize: '15px',
        padding: '20px'
    },
    modalButton: {
        width: '140px'
    },
    buttonsContainer: {
        padding: '0 20px 20px 20px',
        textAlign: 'center'
    },
    buttonContinue: {
        backgroundColor: '#c2a07b'
    },
    buttonClose: {
        marginRight: '25px'
    }
}));

export const UploadFile = ({
    title,
    singleFile,
    dataField,
    disabled,
    upload,
    onFileUploaded,
    uploadButtonText,
    showWarning,
    setShowModal,
    showModal,
    ...props }) => {
    const classes = useStyles();

    const [files, setFiles] = useState([]);

    const handleDrop = useCallback((acceptedFiles) => {
        setFiles((prevFiles) => {
            if (singleFile) return [].concat(acceptedFiles[0]);
            else return [...prevFiles].concat(acceptedFiles);
        });
    }, []);

    const [isUploading, setIsUploading] = useState(false);
    const uploadAllFilesClickHandler = async () => {
        setIsUploading(true);
        for (const file of files) {
            try {
                const data = new FormData();
                data.append(dataField ?? "file", file);
                if (upload) {
                    const success = await upload(data);
                    if (!success) throw "Unable to upload file";
                }
                setFiles((prevFiles) => [...prevFiles].filter(x => x.name !== file.name));
                if (onFileUploaded) onFileUploaded(file, true);

            } catch (e) {
                if (onFileUploaded) onFileUploaded(file, false);
                console.debug(e);
            }
        }

        setIsUploading(false);
    }

    const removeFileClickHandler = (index) => {
        const newFiles = [].concat(files)
        newFiles.splice(index, 1)
        setFiles(newFiles)
    }

    const removeAllFilesClickHandler = () => {
        setFiles([])
    }

    const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({ onDrop: handleDrop, disabled });

    return (
        <div>
            <div
                className={clsx({
                    [classes.dropZone]: true,
                    [classes.dragActive]: isDragActive
                })}
                {...getRootProps()}>
                <input {...getInputProps()} />
                <div>
                    <Typography
                        gutterBottom
                        variant="h6">
                        {title}
                    </Typography>
                    <Typography
                        className={classes.info}
                        color="textSecondary"
                        variant="body1">
                        Drop files here or click
                        {' '}
                        <Link color="textSecondary" underline="always">browse</Link>
                        {' '}
                        thorough your machine
                    </Typography>
                </div>
            </div>
            {files && files.length > 0 && (
                <>
                    <List className={classes.list}>
                        {files.map((file, i) => (
                            <ListItem
                                disableGutters
                                className={classes.item}
                                divider={i < files.length - 1}
                                key={i}>
                                <ListItemIcon>
                                    {!isUploading ?
                                        <PublishIcon />
                                        :
                                        <CircularProgress size="1.6em" color="primary" />
                                    }
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    primaryTypographyProps={{ variant: 'body1' }}
                                />
                                {!isUploading && !singleFile &&
                                    <Tooltip title="Remove">
                                        <IconButton
                                            color="primary"
                                            edge="end"
                                            onClick={() => removeFileClickHandler(i)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                            </ListItem>
                        ))}
                    </List>
                    <div className={classes.actions}>
                        {!singleFile &&
                            <Button
                                color="primary"
                                className={classes.removeButton}
                                size="small"
                                disabled={disabled && isUploading}
                                onClick={removeAllFilesClickHandler}>
                                Remove all
                            </Button>
                        }
                        <Button
                            color="primary"
                            size="small"
                            variant="contained"
                            disabled={disabled && isUploading}
                            onClick={() => {
                                showWarning ?
                                    setShowModal(!showModal)
                                : uploadAllFilesClickHandler();
                            }}>
                            {uploadButtonText ?? (singleFile ? "Upload file" : "Upload files")}
                        </Button>
                        {
                            showWarning &&
                            <Modal open={ showModal }>
                                <Box className={ classes.modal }>
                                    <div className={ classes.modalTitle }>
                                        WARNING! You have selected CLEAR and IMPORT
                                    </div>
                                    <div className={ classes.modalText }>
                                        This action will clear the entire table in database and then will import the selected file. Are you sure you want to continue?
                                    </div>
                                    <div className={ classes.buttonsContainer }>
                                        <Button className={ clsx(classes.button, classes.deleteButton, classes.modalButton, classes.buttonClose) } onClick={ () => { setShowModal(!showModal); } }><ClearIcon />&nbsp;Close</Button>
                                        <Button startIcon={<SaveIcon />} className={ clsx(classes.button, classes.modalButton, classes.buttonContinue) } onClick={ () => {
                                            uploadAllFilesClickHandler();
                                            setShowModal(!showModal);
                                        } }>Continue</Button>
                                    </div>
                                </Box>
                            </Modal>
                        }
                    </div>
                </>
            )}
        </div>
    );
}
